import { useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  getActivePlanAssignmentsOptions,
  getManagedPlansOptions,
  getPlanAndAssignmentsOptions,
  getPlanAndAssignmentsQueryKey,
  getPlansSummaryQueryKey,
  getSystemPlansOptions,
  getUsersForLocations,
  getUsersSummaryReportQueryKey,
  getUserSummaryReportQueryKey,
  type ReadonlySubscriptionRequest,
  SystemPlan,
  upsertReadonlySubscriptionMutation,
} from '@cfa/pathway-fe-shared/xp';

import { usePlanLocations } from '@/hooks/usePlanLocations';
import usePlanOwner from '@/hooks/usePlanOwner';

import { getBroadQueryKeyId } from '@/utils/getBroadQueryKeyId';
import { itemByLanguage } from '@/utils/language';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { routes } from '@/constants/routes';

import { EnableCompliancePlanModal } from './EnableCompliancePlanModal';

import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';
import { isPlanOwnerEligible } from '@/routes/TrainingPlans/isPlanOwnerEligible';

type PlanIdentifier = Pick<SystemPlan, 'id' | 'name'>;
interface UseAddReadOnlyPlanOptions {
  /** Called when user cancels the compliance confirmation modal */
  onCancel?: () => void;
}

interface UseAddReadOnlyPlanReturn {
  /** Subscribe to a read-only plan. Shows modal for compliance, executes directly otherwise. */
  subscribeReadonlyPlan: (plan: SystemPlan) => void;
  unsubscribeReadonlyPlan: (plan: PlanIdentifier) => Promise<void>;
  isPending: boolean;
  pendingPlanId: string | undefined;
  /** Whether the compliance confirmation modal is open */
  complianceModalIsOpen: boolean;
  /** Call to close the compliance modal without confirming */
  onComplianceModalClose: () => void;
  /** Render this where you want the modal content to appear (inside a <PathwayModal>) */
  complianceModal: React.ReactNode;
}

/**
 * useAddReadOnlyPlan - Hook to manage read-only plan subscriptions.
 * Shows compliance modal for compliance plans, handles direct mutation otherwise.
 */
export const useAddReadOnlyPlan = (
  options: UseAddReadOnlyPlanOptions = {},
): UseAddReadOnlyPlanReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocationsByPlan } = usePlanLocations();
  const { shouldSelectOwner, planOwners, resolvePlanOwner } = usePlanOwner({
    isEligible: isPlanOwnerEligible,
  });
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<SystemPlan | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const { assignTeamMembers, isPending: isAssignPending } =
    useAssignTeamMembers();

  const clearSubscriptionCaches = async () => {
    // For the remaining keys, wiping the cache is sufficient — they don't have
    // active observers feeding a Jotai atom, and we just need them to re-fetch
    // fresh on their next mount.
    const staleKeys = [
      getActivePlanAssignmentsOptions().queryKey,
      getSystemPlansOptions().queryKey,
      [getBroadQueryKeyId(getUsersSummaryReportQueryKey)],
      [getBroadQueryKeyId(getUserSummaryReportQueryKey)],
      [getBroadQueryKeyId(getPlanAndAssignmentsQueryKey)],
      [getBroadQueryKeyId(getPlansSummaryQueryKey)],
      getManagedPlansOptions().queryKey,
    ];

    for (const queryKey of staleKeys) {
      queryClient.removeQueries({ queryKey, type: 'all' });
    }

    // Refetch the managed plans query while it has an active observer so that
    // useGetManagePlansData picks up the change and pushes fresh data into the
    // DataView atom. removeQueries alone won't trigger a re-fetch because
    // refetchOnMount is false globally.
    await queryClient.refetchQueries({
      queryKey: getManagedPlansOptions({ query: { status: 'ACTIVE' } })
        .queryKey,
      type: 'all',
    });
  };

  const requirePlanId = (
    source: Pick<SystemPlan, 'id'>,
    context: string,
  ): string => {
    if (!source.id) {
      throw new Error(`Missing plan id in ${context}`);
    }
    return source.id;
  };

  const {
    mutateAsync: upsertReadonlySubscriptionAsync,
    isPending,
    variables,
  } = useMutation({
    ...upsertReadonlySubscriptionMutation(),
    onError: error => {
      console.log('Error upserting readonly subscription', error);
      datadogRum.addError(error);
    },
  });

  const upsertForAllOwners = (
    planId: string,
    body: Omit<ReadonlySubscriptionRequest, 'ownerId' | 'locationNumber'>,
  ) => {
    // Multi-owner staff: subscribe/unsubscribe for every creatable owner.
    // Licensees and single-owner users: only the resolved (active) owner —
    // matching pre-ownerId behavior that targeted the active location only.
    const ownerIds = shouldSelectOwner
      ? [...new Set(planOwners.map(owner => owner.ownerId))]
      : [resolvePlanOwner().ownerId].filter((id): id is string => !!id);

    if (ownerIds.length === 0) {
      throw new Error(
        'Owner could not be resolved: authentication may not have loaded yet.',
      );
    }

    return ownerIds.map(ownerId =>
      upsertReadonlySubscriptionAsync({
        path: { planId },
        body: { ...body, ownerId },
      }),
    );
  };

  const subscribeReadonlyPlan = async (plan: SystemPlan): Promise<void> => {
    const isCompliance = plan.category === 'compliance' && plan.readonly;

    // Compliance plans show modal first
    if (isCompliance) {
      setSelectedPlan(plan);
      return;
    }

    try {
      const planId = requirePlanId(plan, 'subscribeReadonlyPlan');
      await Promise.all(upsertForAllOwners(planId, { optedIn: true }));
      await clearSubscriptionCaches();
    } catch (e) {
      console.log('Error subscribing to readonly plan', e);
    }

    const isAnchor = plan.type === 'ANCHOR' && plan.readonly;
    const planName = itemByLanguage(plan.name);

    const toastMessage = isAnchor
      ? shouldSelectOwner
        ? t('Settings.anchorPlanToast.successMessageMultipleOperators', {
            planName,
          })
        : t('Settings.anchorPlanToast.successMessage', { planName })
      : t('Settings.enableCompliancePlanModal.successToast', {
          planName: itemByLanguage(plan.name),
          forOperators: shouldSelectOwner
            ? t('Settings.enableCompliancePlanModal.successToastForOperators')
            : '',
        });

    toast.custom(toastObj => (
      <ToastMessageBlock severity="success" id={toastObj.id}>
        {toastMessage}
      </ToastMessageBlock>
    ));

    const planId = requirePlanId(plan, 'subscribeReadonlyPlan navigate');

    if (isAnchor) {
      navigate(routes.plans.managePlansEdit({ planId }));
    } else {
      navigate(routes.plans.managePlan({ planId }));
    }
  };

  const unsubscribeReadonlyPlan = async (
    plan: PlanIdentifier,
  ): Promise<void> => {
    try {
      await Promise.all(upsertForAllOwners(plan.id, { optedIn: false }));
      await clearSubscriptionCaches();
      toast.custom(toastObj => (
        <ToastMessageBlock severity="success" id={toastObj.id}>
          {t('TrainingPlans.planRemovedToast', {
            planName: itemByLanguage(plan.name),
          })}
        </ToastMessageBlock>
      ));
    } catch (e) {
      console.log('Error unsubscribing from readonly plan', e);
    }
  };

  const handleModalClose = () => {
    setSelectedPlan(null);
    options.onCancel?.();
  };

  /**
   * Fetches all team members for plan locations, then delegates to
   * `assignTeamMembers` to assign all users to the plan.
   */
  const assignAllTeamMembers = async (
    plan: SystemPlan,
    dueDateOffsetInDays?: number,
  ): Promise<void> => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (dueDateOffsetInDays ?? 30));
    const dueDateStr = dueDate.toISOString().split('T')[0] ?? '';
    const locations = getLocationsByPlan(plan);
    const planId = requirePlanId(plan, 'assignAllTeamMembers');

    if (locations.length === 0) {
      datadogRum.addError(
        new Error(
          `No locations available for compliance plan assignment: ${planId}`,
        ),
      );
      return;
    }

    const usersResponse = await getUsersForLocations({
      query: { locationNumbers: locations },
    });

    const allUserIds = (usersResponse.data ?? [])
      .map(u => u.adId)
      .filter(Boolean) as string[];

    if (allUserIds.length === 0) return;

    await assignTeamMembers({
      planId,
      userIds: allUserIds,
      dueDate: dueDateStr,
    });
  };

  /**
   * Confirms enabling a compliance plan.
   *
   * Assigns the plan to all current team members immediately after subscribe.
   * Backend handles auto-assignment for new employees and recertification cycles.
   */
  const handleModalConfirm = async () => {
    if (!selectedPlan) return;

    // Intentionally do NOT call subscribeReadonlyPlan so its
    // navigate/toast don't fire — we handle both directly here after all work is done.
    setIsConfirming(true);

    try {
      const modalPlanId = requirePlanId(selectedPlan, 'handleModalConfirm');
      const subscriptionPromises = upsertForAllOwners(modalPlanId, {
        optedIn: true,
      });

      const results = await Promise.allSettled(subscriptionPromises);

      const failedResults = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );

      failedResults.forEach(result => {
        datadogRum.addError(
          result.reason instanceof Error
            ? result.reason
            : new Error(String(result.reason)),
          { section: 'readonlySubscription', planId: selectedPlan.id },
        );
      });

      if (failedResults.length > 0 && failedResults.length === results.length) {
        // All subscription attempts failed; fall back to generic error handling.
        throw new Error('All readonly subscription requests failed');
      }

      await clearSubscriptionCaches();

      try {
        await assignAllTeamMembers(selectedPlan);
      } catch (assignError) {
        datadogRum.addError(
          assignError instanceof Error
            ? assignError
            : new Error(String(assignError)),
          { section: 'complianceAutoAssign', planId: modalPlanId },
        );
      }
      queryClient.removeQueries({
        queryKey: getPlanAndAssignmentsOptions({
          path: { planId: modalPlanId },
        }).queryKey,
      });

      const planName = itemByLanguage(selectedPlan.name);
      navigate(routes.plans.managePlan({ planId: modalPlanId }));
      toast.custom(toastObj => (
        <ToastMessageBlock severity="success" id={toastObj.id}>
          {t('Settings.enableCompliancePlanModal.successToast', {
            planName,
            forOperators: shouldSelectOwner
              ? t('Settings.enableCompliancePlanModal.successToastForOperators')
              : '',
          })}
        </ToastMessageBlock>
      ));
    } catch (e) {
      console.log('Error subscribing to readonly plan', e);
      setSelectedPlan(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const complianceModal = selectedPlan ? (
    <EnableCompliancePlanModal
      plan={selectedPlan}
      isPending={isPending || isConfirming || isAssignPending}
      onClose={handleModalClose}
      onConfirm={handleModalConfirm}
    />
  ) : null;

  return {
    subscribeReadonlyPlan,
    unsubscribeReadonlyPlan,
    isPending,
    pendingPlanId: variables?.path.planId,
    complianceModalIsOpen: !!selectedPlan,
    onComplianceModalClose: handleModalClose,
    complianceModal,
  };
};
